import React, { ReactNode, useState } from 'react';
import { Form, Button, Alert, Card, Col, Row } from 'react-bootstrap';
import { useForm, usePage } from '@inertiajs/react';

import AuthedLayout from '../Layouts/Authed';

interface Profile {
	name: string;
	email: string;
	role: string;
	agency_name: string | null;
}

interface Props {
	profile: Profile;
}

const ProfilePage = ({ profile }: Props) => {
	const { flash } = usePage().props as any;
	const [editingProfile, setEditingProfile] = useState(false);

	const profileForm = useForm({
		name: profile.name,
		email: profile.email,
		current_password: '',
	});

	const passwordForm = useForm({
		current_password: '',
		password: '',
		password_confirmation: '',
	});

	const [password, setPassword] = useState('');
	const [passwordConfirmation, setPasswordConfirmation] = useState('');
	const [currentPassword, setCurrentPassword] = useState('');
	const passwordMismatch = !!passwordConfirmation && password !== passwordConfirmation;
	const passwordFormIncomplete = !currentPassword || !password || !passwordConfirmation || passwordMismatch;

	const handleProfileSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		profileForm.post('/profile', {
			preserveScroll: true,
			onSuccess: () => {
				setEditingProfile(false);
				profileForm.setData('current_password', '');
			},
		});
	};

	const handlePasswordSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (passwordMismatch) return;
		passwordForm.post('/profile/password', {
			preserveScroll: true,
			onSuccess: () => {
				passwordForm.reset();
				setCurrentPassword('');
				setPassword('');
				setPasswordConfirmation('');
			},
		});
	};

	const handleCancelEdit = () => {
		setEditingProfile(false);
		profileForm.setData({
			name: profile.name,
			email: profile.email,
			current_password: '',
		});
		profileForm.clearErrors();
	};

	return (
		<Row>
			<Col lg={6}>
			{flash?.success && (
				<Alert variant="success" className="mb-4">
					{flash.success}
				</Alert>
			)}

			<Card className="mb-4">
				<Card.Header className="d-flex justify-content-between align-items-center">
					<span>Profile Information</span>
					{!editingProfile && (
						<Button
							variant="outline-primary"
							size="sm"
							onClick={() => setEditingProfile(true)}
						>
							Edit
						</Button>
					)}
				</Card.Header>
				<Card.Body>
					{editingProfile ? (
						<Form onSubmit={handleProfileSubmit}>
							<Form.Group className="mb-3">
								<Form.Label>Name</Form.Label>
								<Form.Control
									type="text"
									value={profileForm.data.name}
									onChange={e => profileForm.setData('name', e.target.value)}
									isInvalid={!!profileForm.errors.name}
								/>
								<Form.Control.Feedback type="invalid">
									{profileForm.errors.name}
								</Form.Control.Feedback>
							</Form.Group>

							<Form.Group className="mb-3">
								<Form.Label>Email</Form.Label>
								<Form.Control
									type="email"
									value={profileForm.data.email}
									onChange={e => profileForm.setData('email', e.target.value)}
									isInvalid={!!profileForm.errors.email}
								/>
								<Form.Control.Feedback type="invalid">
									{profileForm.errors.email}
								</Form.Control.Feedback>
								<Form.Text className="text-muted">
									This is also your login email.
								</Form.Text>
							</Form.Group>

							<Form.Group className="mb-3">
								<Form.Label>Current Password</Form.Label>
								<Form.Control
									type="password"
									value={profileForm.data.current_password}
									onChange={e => profileForm.setData('current_password', e.target.value)}
									isInvalid={!!profileForm.errors.current_password}
								/>
								<Form.Control.Feedback type="invalid">
									{profileForm.errors.current_password}
								</Form.Control.Feedback>
								<Form.Text className="text-muted">
									Required to confirm changes.
								</Form.Text>
							</Form.Group>

							<div className="d-flex gap-2">
								<Button type="submit" disabled={profileForm.processing}>
									{profileForm.processing ? 'Saving...' : 'Save'}
								</Button>
								<Button variant="outline-secondary" onClick={handleCancelEdit}>
									Cancel
								</Button>
							</div>
						</Form>
					) : (
						<div>
							<div className="mb-3">
								<div className="text-muted small">Name</div>
								<div>{profile.name}</div>
							</div>
							<div className="mb-3">
								<div className="text-muted small">Email</div>
								<div>{profile.email}</div>
							</div>
							{profile.agency_name && (
								<>
								<div className="mb-3">
									<div className="text-muted small">Agency</div>
									<div>{profile.agency_name}</div>
								</div>
								<p className="text-muted small mb-0">
									To update your agency name, please{' '}
									<a href="https://iavaluations.com/contact/" target="_blank">contact us</a>.
								</p>
								</>
							)}
						</div>
					)}
				</Card.Body>
			</Card>

			<Card>
				<Card.Header>Change Password</Card.Header>
				<Card.Body>
					<Form onSubmit={handlePasswordSubmit}>
						<Form.Group className="mb-3">
							<Form.Label>Current Password</Form.Label>
							<Form.Control
								type="password"
								value={currentPassword}
								onChange={e => { setCurrentPassword(e.target.value); passwordForm.setData('current_password', e.target.value); }}
								isInvalid={!!passwordForm.errors.current_password}
							/>
							<Form.Control.Feedback type="invalid">
								{passwordForm.errors.current_password}
							</Form.Control.Feedback>
						</Form.Group>

						<Form.Group className="mb-3">
							<Form.Label>New Password</Form.Label>
							<Form.Control
								type="password"
								value={password}
								onChange={e => { setPassword(e.target.value); passwordForm.setData('password', e.target.value); }}
								isInvalid={!!passwordForm.errors.password}
							/>
							<Form.Control.Feedback type="invalid">
								{passwordForm.errors.password}
							</Form.Control.Feedback>
						</Form.Group>

						<Form.Group className="mb-3">
							<Form.Label>Confirm New Password</Form.Label>
							<Form.Control
								type="password"
								value={passwordConfirmation}
								onChange={e => { setPasswordConfirmation(e.target.value); passwordForm.setData('password_confirmation', e.target.value); }}
								isInvalid={passwordMismatch}
							/>
							<Form.Control.Feedback type="invalid">
								Passwords do not match.
							</Form.Control.Feedback>
						</Form.Group>

						<Button type="submit" disabled={passwordFormIncomplete}>
							Update Password
						</Button>
					</Form>
				</Card.Body>
			</Card>
			</Col>
		</Row>
	);
};

ProfilePage.layout = (page: ReactNode) => <AuthedLayout pageTitle="Manage Profile">{page}</AuthedLayout>;

export default ProfilePage;
